import { gql, useQuery } from '@apollo/client';
import { Spin } from 'antd';
import { get } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  createMasonryCellPositioner,
  Masonry,
} from 'react-virtualized';
import ImageMeasurer from 'react-virtualized-image-measurer';
import 'react-virtualized/styles.css';

import { AppHeader } from '../../components/header';
import { ProductCard } from '../../components/productCard';
import { useWindowSize } from '../../hooks';

import './style.scss';

const PRODUCT_LIST = gql`
  query products($categoryKey: ID!) {
    category(key: $categoryKey) {
      id
      name
    }
    products(categoryKey: $categoryKey) {
      id
      name
      mainCover {
        id
        url
      }
      price
      promotion
      inStock
    }
  }
`;

const WIDTH = 174;

export const CatalogCategoryView = ({ catalog: key }) => {
  const catalog = key.replace(/%2F/g, '/');
  const { data } = useQuery(PRODUCT_LIST, {
    variables: {
      categoryKey: catalog,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [winWidth] = useWindowSize();

  const [itemWidth, columnCount] = useMemo(() => {
    const PADDING = 12;
    // const add = winWidth - 600
    if (winWidth < 600) {
      return [(winWidth - PADDING) / 2, 2];
    }
    const off = Math.floor((winWidth - 600) / 200);
    return [(winWidth - PADDING) / (2 + off), 2 + off];
  }, [winWidth]);

  const products = get(data, 'products') || [];
  const cat = get(data, 'category');

  const [cache] = useState(
    () =>
      new CellMeasurerCache({
        defaultHeight: 200,
        defaultWidth: itemWidth,
        fixedWidth: true,
      })
  );

  const [cellPositioner] = useState(() =>
    createMasonryCellPositioner({
      cellMeasurerCache: cache,
      columnCount: columnCount,
      columnWidth: itemWidth,
      spacer: 0,
    })
  );

  const ref = useRef();
  useEffect(() => {
    cellPositioner.reset({
      cellMeasurerCache: cache,
      columnCount: columnCount,
      columnWidth: itemWidth,
      spacer: 0,
    });
    ref.current.recomputeCellPositions();
  }, [winWidth]);

  const cellRenderer = ({ index, key, parent, style }) => {
    return (
      <CellMeasurer cache={cache} index={index} key={index} parent={parent}>
        <div style={style}>
          <ProductItem data={products[index]} />
        </div>
      </CellMeasurer>
    );
  };

  return (
    <div className="catalog-category-view">
      <AppHeader
        back="/catalog"
        title={get(cat, 'name') ? get(cat, 'name') : <Spin size="small" />}
      />
      <div className="catalog-product-list">
        <AutoSizer>
          {({ width, height }) => (
            <ImageMeasurer
              items={products}
              image={(item) => (item.mainCover ? item.mainCover.url : null)}
              defaultHeight={200}
              defaultWidth={WIDTH}
            >
              {({ itemsWithSizes }) => (
                <Masonry
                  style={{ outline: 'none', paddingBottom: 90 }}
                  ref={ref}
                  cellCount={itemsWithSizes.length}
                  cellMeasurerCache={cache}
                  cellPositioner={cellPositioner}
                  cellRenderer={cellRenderer}
                  height={height}
                  width={width}
                />
              )}
            </ImageMeasurer>
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

const ProductItem = ({ index, data: item, onImageLoad }) => {
  return (
    <ProductCard
      productId={item.id}
      coverUrl={get(item, 'mainCover.url')}
      name={item.name}
      onImageLoad={onImageLoad}
      inStock={item.inStock}
      price={item.price}
      promotion={item.promotion}
    />
  );
};
